import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  ArrowRight,
  ArrowUpRight,
  Check,
  Share2,
  Loader2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// 5 questions. Each option contributes to one or two archetypes.
// Archetype keys: hero, sage, outlaw, lover, creator, caregiver, explorer, magician
const QUESTIONS = [
  {
    q: "When a customer tells a friend about your brand, what do they say?",
    options: [
      { label: "This is what real quality feels like.", weights: { lover: 2, creator: 1 } },
      { label: "These people actually know what they're talking about.", weights: { sage: 2 } },
      { label: "They challenge how the whole industry works.", weights: { outlaw: 2 } },
      { label: "They make me feel taken care of.", weights: { caregiver: 2 } },
      { label: "They make me feel like I'm becoming a better version of myself.", weights: { hero: 2 } },
    ],
  },
  {
    q: "Your competitors are doing all the obvious things. You'd rather:",
    options: [
      { label: "Do the obvious things, but ten times better.", weights: { hero: 2, sage: 1 } },
      { label: "Teach the customer why the obvious things are wrong.", weights: { sage: 2 } },
      { label: "Break the obvious rule on purpose and own it.", weights: { outlaw: 2 } },
      { label: "Make something nobody else would have the patience to make.", weights: { creator: 2 } },
      { label: "Take the customer somewhere unexpected.", weights: { explorer: 2, magician: 1 } },
    ],
  },
  {
    q: "Your homepage hero feeling is closest to:",
    options: [
      { label: "Calm authority. We've thought about this longer than they have.", weights: { sage: 2 } },
      { label: "Warm and human. We're on your side.", weights: { caregiver: 2, lover: 1 } },
      { label: "Bold and a bit dangerous. We don't ask permission.", weights: { outlaw: 2 } },
      { label: "Wide-eyed wonder. Something new is possible here.", weights: { magician: 2, explorer: 1 } },
      { label: "Quiet craft. Look closer.", weights: { creator: 2 } },
    ],
  },
  {
    q: "If your brand had to choose one enemy, it would be:",
    options: [
      { label: "Mediocrity and 'good enough'.", weights: { hero: 2 } },
      { label: "Mass-produced anything.", weights: { creator: 2, lover: 1 } },
      { label: "The old guard that's gatekeeping the industry.", weights: { outlaw: 2 } },
      { label: "The misinformation in our category.", weights: { sage: 2 } },
      { label: "Cold, transactional, soulless service.", weights: { caregiver: 2 } },
    ],
  },
  {
    q: "After using your product, your customer feels:",
    options: [
      { label: "More capable than they walked in.", weights: { hero: 2 } },
      { label: "Smarter and more informed.", weights: { sage: 2 } },
      { label: "Like they've found their people.", weights: { lover: 1, outlaw: 1, caregiver: 1 } },
      { label: "Like they've discovered something new about themselves.", weights: { explorer: 2 } },
      { label: "Like the world just got a little more magical.", weights: { magician: 2 } },
    ],
  },
];

const ARCHETYPES = {
  hero: {
    name: "The Hero",
    color: "#D4A53A",
    tagline: "You exist to help customers prove what they're capable of.",
    body: "Hero brands are about mastery, courage and triumph. Your customer is on a journey — you're the catalyst, not the destination. Be bold, direct and aspirational, but never patronising. The fastest way to ruin a Hero brand is to be vague about who you're for.",
    examples: "Nike. Duolingo. Strava.",
    voice: "Direct. Crisp. Action-verbs. No hedging.",
    pitfalls: "Sounding like motivational posters. Mistaking volume for conviction.",
  },
  sage: {
    name: "The Sage",
    color: "#0A1E3F",
    tagline: "You exist to help customers see clearly.",
    body: "Sage brands trade in clarity. You're the calm authority in a noisy category — the one who shows up with data, frameworks and 'here's what's actually going on'. Don't be afraid to be the slowest, most thoughtful voice in the room. That's the differentiator.",
    examples: "The Economist. Stripe. Bloomberg.",
    voice: "Precise. Measured. Confident in plain language.",
    pitfalls: "Becoming preachy or jargon-heavy. Performing expertise instead of demonstrating it.",
  },
  outlaw: {
    name: "The Outlaw",
    color: "#0A1E3F",
    tagline: "You exist to disrupt what everyone else has accepted.",
    body: "Outlaw brands change rules. You name what's broken in the category and refuse to play along. Be specific about what you're against — vagueness reads as posing. The right people will lean in, the wrong people will leave, both are correct.",
    examples: "Harley-Davidson. Liquid Death. Patagonia.",
    voice: "Punchy. Unapologetic. Often a little funny.",
    pitfalls: "Edginess for its own sake. Picking fights you can't back up.",
  },
  lover: {
    name: "The Lover",
    color: "#D4A53A",
    tagline: "You exist to make beauty and intimacy feel possible.",
    body: "Lover brands are about sensory pleasure, taste and connection. You sell the feeling of being chosen and choosing. Don't apologise for being indulgent — the whole point is that some things are worth a slow Saturday morning and a long sentence.",
    examples: "Aesop. Glossier. Le Labo.",
    voice: "Sensorial. Lyrical. Comfortable in white space.",
    pitfalls: "Being precious instead of warm. Confusing aesthetic with substance.",
  },
  creator: {
    name: "The Creator",
    color: "#0A1E3F",
    tagline: "You exist to give people the tools and permission to make something.",
    body: "Creator brands honour craft and originality. You see customers as makers, not consumers. The product is a launchpad. Talk about the work, the process, the unreasonable attention to detail — and trust your customer to recognise it.",
    examples: "Notion. Patreon. Moleskine.",
    voice: "Curious. Specific. Generous with knowledge.",
    pitfalls: "Inside-baseball that excludes new users. Romanticising suffering.",
  },
  caregiver: {
    name: "The Caregiver",
    color: "#D4A53A",
    tagline: "You exist to make hard things feel softer.",
    body: "Caregiver brands lower friction and lower fear. You don't pretend the customer's problem is trivial — you take it seriously and make it manageable. Warmth without saccharine, attention without theatre. This is one of the hardest archetypes to get right.",
    examples: "Headspace. Calm. Volvo.",
    voice: "Warm. Slow. Reassuring without being preachy.",
    pitfalls: "Drifting into therapy-speak. Over-promising safety.",
  },
  explorer: {
    name: "The Explorer",
    color: "#0A1E3F",
    tagline: "You exist to take customers somewhere they couldn't reach alone.",
    body: "Explorer brands are about discovery, autonomy and the open road. You're not selling the destination — you're selling the permission to leave. Avoid trying to over-package the journey. Your customer wants room, not a tour guide.",
    examples: "Airbnb. The North Face. Land Rover.",
    voice: "Spacious. Curious. Unsentimental.",
    pitfalls: "Empty wanderlust language. Treating exploration as a vibe instead of a verb.",
  },
  magician: {
    name: "The Magician",
    color: "#D4A53A",
    tagline: "You exist to turn what feels impossible into something inevitable.",
    body: "Magician brands transform. You promise the customer that after this, things will not be the same — a haircut, a quarter, a relationship. Earn it with specifics. The line between Magician and bullshit is whether the transformation actually happens.",
    examples: "Apple. Disney. Tesla.",
    voice: "Quietly assured. Big claims, backed up.",
    pitfalls: "Promising more than the product delivers. Mistaking spectacle for transformation.",
  },
};

const BrandVibeCheckPage = () => {
  const [step, setStep] = useState(0); // 0..QUESTIONS.length-1 then "result"
  const [scores, setScores] = useState({});
  const [email, setEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState("idle"); // idle | loading | done | error
  const [emailError, setEmailError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = "Brand Vibe Check · Find your brand archetype · The Story Assembly";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content") || "";
    if (meta) {
      meta.setAttribute(
        "content",
        "Free 5-question quiz that reveals your brand archetype — Hero, Sage, Outlaw, Lover, Creator and more. Get a share-ready result card. From The Story Assembly."
      );
    }
    return () => {
      document.title = prev;
      if (meta) meta.setAttribute("content", prevDesc);
    };
  }, []);

  const pick = (optIdx) => {
    const opt = QUESTIONS[step].options[optIdx];
    const next = { ...scores };
    for (const [k, v] of Object.entries(opt.weights)) {
      next[k] = (next[k] || 0) + v;
    }
    setScores(next);
    setStep(step + 1);
  };

  const restart = () => {
    setStep(0);
    setScores({});
  };

  const topKey =
    Object.entries(scores).sort((a, b) => b[1] - a[1])[0]?.[0] || "hero";
  const result = ARCHETYPES[topKey];

  const isDone = step >= QUESTIONS.length;
  const progress = Math.min(100, (step / QUESTIONS.length) * 100);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setEmailStatus("loading");
    setEmailError("");
    try {
      const res = await fetch(`${API}/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "Couldn't subscribe");
      }
      setEmailStatus("done");
    } catch (err) {
      setEmailError(err.message || "Something went wrong");
      setEmailStatus("error");
    }
  };

  const shareCopy = async () => {
    const siteUrl = "https://storyassembly.in/brand-vibe-check";
    const text = `My brand archetype is ${result.name} — "${result.tagline}"\n\nWhat's yours? ${siteUrl}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // silent
    }
  };

  return (
    <div data-testid="vibe-check-page" className="min-h-screen bg-[#F4F1EA] text-[#1C1A17]">
      <header className="border-b border-[#DCD7C9]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="https://customer-assets.emergentagent.com/job_tale-builder-110/artifacts/rxbavk25_ChatGPT%20Image%20May%2029%2C%202025%2C%2010_23_27%20PM.png"
              alt="The Story Assembly"
              className="h-9 w-9 rounded-md object-cover ring-1 ring-[#1C1A17]/10"
            />
            <span className="font-serif text-xl tracking-tight">
              The <span className="italic font-light">Story</span>{" "}
              <span className="text-[#D4A53A]">Assembly</span>
            </span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24">
        {!isDone ? (
          <>
            <div className="font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#D4A53A]">
              Question {step + 1} of {QUESTIONS.length}
            </div>
            <div className="mt-3 h-1 w-full bg-[#DCD7C9] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#0A1E3F] transition-all duration-500"
                style={{ width: `${progress}%` }}
                data-testid="vibe-progress"
              />
            </div>
            <h1 className="font-serif text-3xl md:text-5xl tracking-tighter mt-10 text-[#0A1E3F] leading-snug">
              {QUESTIONS[step].q}
            </h1>
            <div className="mt-10 space-y-3">
              {QUESTIONS[step].options.map((opt, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => pick(i)}
                  data-testid={`vibe-opt-${step}-${i}`}
                  className="w-full text-left rounded-md border border-[#DCD7C9] bg-white p-5 hover:border-[#D4A53A] hover:bg-[#D4A53A]/5 transition-colors group"
                >
                  <span className="font-serif text-[18px] leading-snug text-[#0A1E3F] group-hover:text-[#D4A53A] transition-colors">
                    {opt.label}
                  </span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div data-testid="vibe-result">
            <div className="font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#D4A53A]">
              Your brand archetype
            </div>
            <h1
              className="font-serif text-5xl md:text-7xl tracking-tighter mt-4 leading-[1.02]"
              style={{ color: result.color }}
              data-testid="vibe-archetype-name"
            >
              {result.name}.
            </h1>
            <p className="font-serif italic mt-6 text-2xl md:text-3xl tracking-tight text-[#0A1E3F] leading-snug">
              {result.tagline}
            </p>
            <p className="mt-6 text-[17px] leading-relaxed text-[#1C1A17]/80 max-w-2xl">
              {result.body}
            </p>

            <div className="mt-10 grid sm:grid-cols-3 gap-3">
              <div className="rounded-md border border-[#DCD7C9] p-4 bg-white">
                <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">Brands you're like</div>
                <div className="mt-2 font-serif text-[16px] text-[#0A1E3F] leading-snug">{result.examples}</div>
              </div>
              <div className="rounded-md border border-[#DCD7C9] p-4 bg-white">
                <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">Voice register</div>
                <div className="mt-2 font-serif text-[16px] text-[#0A1E3F] leading-snug">{result.voice}</div>
              </div>
              <div className="rounded-md border border-[#DCD7C9] p-4 bg-white">
                <div className="font-mono-display text-[10px] uppercase tracking-[0.2em] text-[#6B655B]">Common pitfalls</div>
                <div className="mt-2 font-serif text-[16px] text-[#0A1E3F] leading-snug">{result.pitfalls}</div>
              </div>
            </div>

            {/* Email gate */}
            <div className="mt-12 bg-[#0A1E3F] text-[#F4F1EA] rounded-md p-7 md:p-9">
              <div className="font-mono-display text-[11px] uppercase tracking-[0.22em] text-[#D4A53A]">
                Want more like this?
              </div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tighter mt-3 leading-snug">
                Get a monthly brand essay in your inbox.
              </h2>
              {emailStatus === "done" ? (
                <div
                  data-testid="vibe-email-success"
                  className="mt-5 inline-flex items-center gap-2 text-[14px] font-mono-display tracking-wider text-[#D4A53A]"
                >
                  <Check size={14} /> You're on the list.
                </div>
              ) : (
                <form onSubmit={submitEmail} className="mt-5 flex gap-2 max-w-md" data-testid="vibe-email-form">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@brand.com"
                    data-testid="vibe-email-input"
                    className="flex-1 rounded-md border border-[#F4F1EA]/20 bg-[#F4F1EA]/10 text-[#F4F1EA] placeholder:text-[#F4F1EA]/50 px-3.5 py-2.5 text-[14px] focus:border-[#D4A53A] focus:outline-none"
                  />
                  <button
                    type="submit"
                    disabled={emailStatus === "loading"}
                    data-testid="vibe-email-submit"
                    className="inline-flex items-center justify-center rounded-md px-4 py-2.5 bg-[#D4A53A] text-[#0A1E3F] text-[13px] font-medium hover:bg-[#D4A53A]/90 disabled:opacity-60"
                  >
                    {emailStatus === "loading" ? <Loader2 size={14} className="animate-spin" /> : "Subscribe"}
                  </button>
                </form>
              )}
              {emailStatus === "error" && (
                <p data-testid="vibe-email-error" className="mt-2 text-[12px] text-[#D4A53A]">
                  {emailError}
                </p>
              )}

              {/* Share + Generator CTA */}
              <div className="mt-7 pt-6 border-t border-[#F4F1EA]/15 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={shareCopy}
                  data-testid="vibe-share-btn"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#F4F1EA]/10 hover:bg-[#D4A53A]/20 border border-[#F4F1EA]/15 text-[#F4F1EA] text-[13px] transition-all"
                >
                  <Share2 size={13} /> {copied ? "Copied!" : "Share result"}
                </button>
                <Link
                  to="/#demo"
                  data-testid="vibe-cta-generator"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 bg-[#D4A53A] text-[#0A1E3F] text-[13px] font-medium"
                >
                  <Sparkles size={13} /> Now build the brand story
                </Link>
                <Link
                  to="/blog"
                  data-testid="vibe-cta-blog"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[#F4F1EA] text-[13px] hover:text-[#D4A53A]"
                >
                  Read the Journal <ArrowUpRight size={13} />
                </Link>
              </div>
            </div>

            <button
              type="button"
              onClick={restart}
              data-testid="vibe-restart"
              className="mt-10 inline-flex items-center gap-1.5 text-[12px] font-mono-display uppercase tracking-[0.2em] text-[#6B655B] hover:text-[#0A1E3F]"
            >
              ← Retake the quiz
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default BrandVibeCheckPage;
